import type { Ref } from "vue";
import type { Order } from "@nwt/norce-pwa/dist/runtime/types/api/checkout/orders/orders";
import { findMarketingOptOut } from "@nwt/norce-pwa/marketingOptOut";

/**
 * [GF-77] Keeps the checkout's marketing opt-out in step with the NCO order. The
 * webhook reads it from the order, and Checkout recreates orders, so the choice
 * is re-applied to every new order id.
 */
export const useCheckoutMarketingOptOut = (
	order: Ref<Order | null>,
	consentText: () => string
) => {
	const { norceOrders } = useNorcePwa();
	const { getCheckoutQueryParams } = useCheckoutChannel();

	const optedOut = ref(false);
	const isSaving = ref(false);
	const hasError = ref(false);
	let syncedOrderId = "";

	const save = async (orderId: string, checked: boolean) => {
		isSaving.value = true;
		hasError.value = false;
		try {
			await norceOrders.updateMarketingOptOut(
				{ orderId, ...getCheckoutQueryParams() },
				{ checked, text: consentText() }
			);
			return true;
		} catch (err) {
			console.error("[Checkout] marketing opt-out not saved:", err);
			hasError.value = true;
			return false;
		} finally {
			isSaving.value = false;
		}
	};

	// A failure on an order that was replaced meanwhile isn't rolled back: the
	// watcher already applied the current choice to the new order.
	const isCurrentOrder = (orderId: string) => order.value?.id === orderId;

	const handleOptOutChange = async (checked: boolean) => {
		optedOut.value = checked;
		// No order yet: the watcher below applies it once there is one.
		const orderId = order.value?.id;
		if (orderId && !(await save(orderId, checked)) && isCurrentOrder(orderId)) {
			optedOut.value = !checked;
		}
	};

	// A reloaded checkout reuses its order, so that order's consent wins; a new
	// order starts without one and gets the shopper's current choice.
	watch(
		() => order.value?.id,
		async (orderId) => {
			if (!orderId || orderId === syncedOrderId) return;
			syncedOrderId = orderId;
			const existing = findMarketingOptOut(order.value?.consents);
			if (existing) {
				optedOut.value = existing.checked;
			} else if (
				optedOut.value &&
				!(await save(orderId, true)) &&
				isCurrentOrder(orderId)
			) {
				optedOut.value = false;
			}
		},
		{ immediate: true }
	);

	return { optedOut, isSaving, hasError, handleOptOutChange };
};
